import { ValueOf } from "@/types/base";
import { GenerateBizType } from "@/types/creator";
import {
  AIMetaType,
  InpaintingMeta,
  LandscapeSynthesisMeta,
  SuperResolutionMeta,
  Txt2ImgMetaData,
  Txt2ImgOutpaintingMeta,
  VariationType,
} from "@/types/meta";
import { HIGH_RES_ADVANCE_MODE_MAX_WH } from "@/utils/constants";
import { checkAIMeta } from "@/utils/misc";
import { isAuthUser, isVip } from "@/utils/status";
import { ABCStore } from "@noli/business";
import { cloneDeep } from "lodash";
import { makeObservable, observable, runInAction } from "mobx";
import { isNoliboxModel } from "../components/common/ModelSwitch/util";
import {
  DEFAULT_GUIDANCE_SCALE,
  DEFAULT_NUM_STEPS,
  ModelTypes,
  NoliboxModelTypes,
  NOLIBOX_MODEL_TYPES,
  SDSampler,
} from "../constant";
import { parseModelType } from "../utils/meta";

export type AIParamsMeta =
  | AIMetaType
  | SuperResolutionMeta
  | LandscapeSynthesisMeta
  | InpaintingMeta;
export type AIParamsData = {
  url: string;
  text: string;
  negative_prompt: string;
  sampler: SDSampler;
  fidelity: number;
  w: number;
  h: number;
  num_steps: number;
  guidance_scale: number;
  use_circular: boolean;
  seed: number;
  version: string;
  variations: VariationType[];
  mask_url: string;
  use_refine: boolean;
  refine_fidelity: number;
  highres_info: undefined | Txt2ImgMetaData["highres_info"];
} & Partial<AIMetaType["data"]>;
export type AIParamsType = AIParamsMeta["type"];
interface IAIParamsStore {
  importMetaParamStr: string;
  type: AIParamsMeta["type"] | null;
  model: NoliboxModelTypes;
  data: AIParamsData;
}

// 全量字段
const defaultParamsData: AIParamsData = {
  url: "",
  text: "",
  negative_prompt: "",
  sampler: "k_euler",
  fidelity: 0.2,
  w: 512,
  h: 512,
  num_steps: DEFAULT_NUM_STEPS,
  guidance_scale: DEFAULT_GUIDANCE_SCALE,
  use_circular: false,
  seed: -1,
  version: "v1.5",
  variations: [],
  mask_url: "",
  use_refine: false,
  refine_fidelity: 0.8,
  highres_info: undefined,
};

export const MetaBizTypeMap: Record<AIParamsType, GenerateBizType> = {
  img2img: GenerateBizType.Img2Img,
  "img2img.variation": GenerateBizType.Variation,
  inpainting: GenerateBizType.Erase,
  landscape: GenerateBizType.Landscape,
  sr: GenerateBizType.SuperRsolution,
  txt2img: GenerateBizType.Txt2Img,
  "txt2img.inpainting": GenerateBizType.Replace,
  "txt2img.outpainting": GenerateBizType.Outpainting,
  "txt2img.variation": GenerateBizType.Variation,
};

class AIParamsStore extends ABCStore<IAIParamsStore> implements IAIParamsStore {
  importMetaParamStr = "";
  type: AIParamsType | null = null;
  model: NoliboxModelTypes = "nolibox";
  data: AIParamsData = defaultParamsData;

  get info(): AIParamsStore {
    return this;
  }

  constructor() {
    super();
    makeObservable(this, {
      importMetaParamStr: observable,
      type: observable,
      model: observable,
      data: observable,
    });
  }
}

export const aiParamsStore = new AIParamsStore();
export const setImportMetaParamStr = (pastedText: string) => {
  aiParamsStore.updateProperty("importMetaParamStr", pastedText);
};
export const updateParamsDataValue = (key: keyof AIParamsData, value: ValueOf<AIParamsData>) => {
  runInAction(() => {
    Object.assign(aiParamsStore.data, { [key]: value });
  });
};

export const updateCustomEmbeddings = (value: string) => {
  runInAction(() => {
    const newModels = aiParamsStore.data?.custom_embeddings?.concat() ?? [];

    if (newModels.includes(value)) {
      newModels.splice(newModels.indexOf(value), 1);
      updateParamsDataValue("custom_embeddings", newModels);
    } else {
      newModels.push(value);
      updateParamsDataValue("custom_embeddings", newModels);
    }
  });
};

export const updateVariations = (index: number, key: keyof VariationType, value: number) => {
  runInAction(() => {
    if (aiParamsStore.data.variations?.[index]) {
      aiParamsStore.data.variations[index][key] = value;
    }
  });
};

export function parseMetaToParams(meta: AIParamsMeta) {
  const { data, type } = meta;
  const isAIMeta = checkAIMeta(meta);
  const metaData = cloneDeep(data) as AIParamsData;
  runInAction(() => {
    if (isAIMeta) {
      const modelType = parseModelType(meta);
      console.log("modelType: ", modelType);
      if (modelType && isNoliboxModel(modelType)) {
        aiParamsStore.updateProperty("model", modelType);
      } else {
        // 兼容写法
        if (meta.data.is_anime) {
          aiParamsStore.updateProperty("model", "anime");
        } else {
          aiParamsStore.updateProperty("model", "nolibox");
        }
      }
    } else {
      metaData.w = (data as InpaintingMeta["data"]).wh?.w ?? 512;
      metaData.h = (data as InpaintingMeta["data"]).wh?.h ?? 512;
    }
    aiParamsStore.updateProperty("type", type);

    if (metaData.variations) {
      metaData.variations.forEach((variation) => {
        variation.strength = +variation.strength.toFixed(2);
      });
    }
    aiParamsStore.data = metaData;
    if (type === "txt2img") {
      const useHighRes = isAuthUser() && isVip();
      if (useHighRes) {
        aiParamsStore.data.w = Math.min(aiParamsStore.data.w, HIGH_RES_ADVANCE_MODE_MAX_WH);
        aiParamsStore.data.h = Math.min(aiParamsStore.data.h, HIGH_RES_ADVANCE_MODE_MAX_WH);
      }
    }
  });
}
